import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
} from '@mlm/ui';
import {
  createSupplierInvoice,
  getSupplierInvoice,
  listExpenseCategories,
  listPoBillableReceipts,
  listPoBillingSummary,
  listSupplierCatalog,
  postSupplierInvoice,
  updateSupplierInvoice,
  type Supplier,
  type SupplierInvoiceDetail,
  type SupplierInvoiceLineInput,
  type SupplierInvoiceReceiptRow,
  type SupplierInvoiceRow,
} from '../../lib/api';
import { Money } from './_shared';

/**
 * Phase 3 bill editor + detail panel for Payables. A bill is a DRAFT until
 * posted: lines (item = product × qty × unit cost, category = expense
 * category + amount) and, when it bills a PO, the specific receipts it
 * covers. Every money figure shown here is display-only Number math on the
 * decimal strings the server owns — the server recomputes totals from the
 * lines and computes the match at post time.
 */

// ── display helpers (duplicated from payables.tsx on purpose: exporting
//    non-components from a page module breaks Fast Refresh) ─────────────────

function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** Local-timezone YYYY-MM-DD for `<input type="date">`. */
function toDateInput(iso?: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
}

/** The server's moneyStringSchema takes no thousands separators. */
function sanitizeMoney(v: string): string {
  return v.replace(/,/g, '').trim();
}

/**
 * Server money arrives as 4dp strings ("40.1250"). Seed editable fields with
 * the VALUE untouched — only trailing zeros trimmed for readability — never
 * `Number(x).toFixed(2)`, which would silently reprice a re-saved draft.
 */
function trimMoney(v: string | null | undefined): string {
  if (v == null || v === '') return '';
  const s = String(v);
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

const ERROR_COPY: Record<string, string> = {
  duplicate_supplier_invoice: 'A bill with this number already exists for this supplier.',
  invoice_not_draft: 'Only draft bills can be edited or posted.',
  receipt_already_billed: 'One of the selected receipts is already on another bill.',
  receipts_require_po: 'Pick the PO before linking receipts.',
  receipt_po_mismatch: 'A selected receipt belongs to a different PO.',
  duplicate_line: 'The same product appears twice — combine the lines.',
  draft_has_no_lines: 'Add at least one line before posting.',
  lines_required: 'Add at least one line.',
  invalid_line: 'A line has an invalid quantity or amount.',
  invalid_amount: 'The bill total must be greater than zero.',
  invalid_vat_amount: 'VAT must be less than the bill total.',
  product_not_found: 'A product on this bill no longer exists.',
  category_not_found: 'An expense category on this bill no longer exists.',
  category_inactive: 'An expense category on this bill is inactive.',
  po_supplier_mismatch: 'That PO belongs to a different supplier.',
  po_not_found: 'Purchase order not found.',
  supplier_not_found: 'Supplier not found.',
  accounting_disabled: 'Accounting is switched off for this tenant.',
  po_cancelled: 'The linked PO was cancelled — unlink it before posting.',
  validation_error:
    'A field has an invalid value — check quantities and amounts (up to 4 decimals).',
};

function friendly(error: unknown, fallback: string): string {
  const code = errText(error);
  return code ? (ERROR_COPY[code] ?? code) : fallback;
}

const SELECT_CLS = 'h-9 rounded-md border border-input bg-background px-3 text-sm';
const NUM_CLS = 'h-9 w-24 text-right';

// ── editor line model ───────────────────────────────────────────────────────

type ItemLine = {
  key: string;
  kind: 'item';
  productId: string;
  variantId: string | null;
  label: string;
  qty: string;
  unitCost: string;
  /** Still mirrors the checked receipts; editing it makes it a manual row. */
  fromReceipt: boolean;
};
type CategoryLine = {
  key: string;
  kind: 'category';
  categoryId: string;
  description: string;
  amount: string;
};
type EditorLine = ItemLine | CategoryLine;

const itemKey = (productId: string, variantId: string | null) => `${productId}:${variantId ?? ''}`;

function lineAmount(l: EditorLine): number {
  if (l.kind === 'item') return (Number(l.qty) || 0) * (Number(sanitizeMoney(l.unitCost)) || 0);
  return Number(sanitizeMoney(l.amount)) || 0;
}

function toInput(l: EditorLine): SupplierInvoiceLineInput {
  if (l.kind === 'item') {
    return {
      productId: l.productId,
      variantId: l.variantId,
      qty: Number(l.qty),
      unitCost: sanitizeMoney(l.unitCost),
    };
  }
  return {
    categoryId: l.categoryId,
    description: l.description.trim() || null,
    amount: sanitizeMoney(l.amount),
  };
}

/** Item lines a set of receipts implies: qty summed per (product, variant) at the PO unit cost. */
function linesFromReceipts(
  receipts: Array<Pick<SupplierInvoiceReceiptRow, 'fulfillmentId' | 'lines'>>,
  checked: Set<string>,
): ItemLine[] {
  const agg = new Map<string, ItemLine>();
  for (const r of receipts) {
    if (!checked.has(r.fulfillmentId)) continue;
    for (const l of r.lines) {
      const key = itemKey(l.productId, l.variantId);
      const prev = agg.get(key);
      if (prev) {
        prev.qty = String(Number(prev.qty) + l.qty);
      } else {
        agg.set(key, {
          key,
          kind: 'item',
          productId: l.productId,
          variantId: l.variantId,
          label: l.variantName ? `${l.productName} · ${l.variantName}` : l.productName,
          qty: String(l.qty),
          unitCost: trimMoney(l.unitCost),
          fromReceipt: true,
        });
      }
    }
  }
  return [...agg.values()];
}

/**
 * Re-derive the receipt-driven rows after the checked set changes: untouched
 * prefilled rows follow the receipts (added / updated / dropped), rows the
 * user edited or added by hand are left alone.
 */
function mergeReceiptLines(prev: EditorLine[], fromReceipts: ItemLine[]): EditorLine[] {
  const wanted = new Map(fromReceipts.map((l) => [l.key, l]));
  const next: EditorLine[] = [];
  for (const l of prev) {
    if (l.kind === 'item' && l.fromReceipt) {
      const w = wanted.get(l.key);
      if (w) {
        next.push({ ...l, qty: w.qty, unitCost: w.unitCost });
        wanted.delete(l.key);
      }
      // dropped: receipt unchecked
    } else {
      next.push(l);
      if (l.kind === 'item') wanted.delete(l.key); // a manual row already covers it
    }
  }
  return [...next, ...wanted.values()];
}

// ── editor dialog ───────────────────────────────────────────────────────────

export function BillEditorDialog({
  suppliers,
  initial,
  onClose,
  onSaved,
}: {
  suppliers: Supplier[];
  /** Present when editing a draft; absent when creating. */
  initial?: SupplierInvoiceDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  // The draft's id once it exists — set by a successful create so a failed
  // "post" step (or a retry) never orphans an already-saved draft.
  const [draftId, setDraftId] = useState<string | null>(initial?.id ?? null);
  const editing = draftId != null;
  const [supplierId, setSupplierId] = useState(initial?.supplierId ?? '');
  const [invoiceNo, setInvoiceNo] = useState(initial?.invoiceNo ?? '');
  const [invoiceDate, setInvoiceDate] = useState(toDateInput(initial?.invoiceDate));
  const [dueDate, setDueDate] = useState(initial ? toDateInput(initial.dueDate) : '');
  const [poId, setPoId] = useState(initial?.poId ?? '');
  const [vatOn, setVatOn] = useState(!!initial && Number(initial.vatAmount) > 0);
  const [vatAmount, setVatAmount] = useState(
    initial && Number(initial.vatAmount) > 0 ? trimMoney(initial.vatAmount) : '',
  );
  const [memo, setMemo] = useState(initial?.memo ?? '');
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set((initial?.receipts ?? []).map((r) => r.fulfillmentId)),
  );
  const [lines, setLines] = useState<EditorLine[]>(() => {
    // Rows that still mirror the saved receipts stay receipt-driven, so
    // re-ticking receipts on a reopened draft keeps working.
    const savedReceipts = initial?.receipts ?? [];
    const derived = new Map(
      linesFromReceipts(savedReceipts, new Set(savedReceipts.map((r) => r.fulfillmentId))).map(
        (l) => [l.key, l],
      ),
    );
    return (initial?.lines ?? []).map((l): EditorLine => {
      if (l.kind === 'item') {
        const key = itemKey(l.productId!, l.variantId);
        const d = derived.get(key);
        const unitCost = trimMoney(l.unitCost ?? '0');
        return {
          key,
          kind: 'item',
          productId: l.productId!,
          variantId: l.variantId,
          label: l.variantName ? `${l.productName} · ${l.variantName}` : (l.productName ?? '—'),
          qty: String(l.qty ?? 0),
          unitCost,
          fromReceipt:
            !!d && d.qty === String(l.qty ?? 0) && Number(d.unitCost) === Number(unitCost),
        };
      }
      return {
        key: `cat:${l.id}`,
        kind: 'category',
        categoryId: l.categoryId!,
        description: l.description ?? '',
        amount: trimMoney(l.lineTotal),
      };
    });
  });
  const [pickCatalog, setPickCatalog] = useState('');
  const [pickCategory, setPickCategory] = useState('');
  const [lineHint, setLineHint] = useState<string | null>(null);

  const supplier = suppliers.find((s) => s.id === supplierId) ?? null;

  // Only POs with received-but-unbilled value (server decides) — plus the
  // draft's own PO when editing, even if it is now fully billed by this draft.
  const posQ = useQuery({
    queryKey: ['po-billing-summary', supplierId],
    queryFn: () => listPoBillingSummary(supplierId),
    enabled: !!supplierId,
  });
  const pos = useMemo(() => {
    const rows = (posQ.data ?? []).filter((p) => !p.fullyBilled || p.poId === initial?.poId);
    if (initial?.poId && !rows.some((p) => p.poId === initial.poId)) {
      rows.unshift({
        poId: initial.poId,
        poNumber: initial.poNumber ?? 'PO',
        status: 'completed',
        orderedTotal: '0',
        receivedValue: '0',
        billedTotal: '0',
        unbilledValue: '0',
        fullyBilled: true,
      });
    }
    return rows;
  }, [posQ.data, initial?.poId, initial?.poNumber]);

  const receiptsQ = useQuery({
    queryKey: ['po-billable-receipts', poId],
    queryFn: () => listPoBillableReceipts(poId),
    enabled: !!poId,
  });
  const receipts = receiptsQ.data ?? [];

  const catalogQ = useQuery({
    queryKey: ['supplier-catalog', supplierId],
    queryFn: () => listSupplierCatalog(supplierId),
    enabled: !!supplierId,
  });
  const categoriesQ = useQuery({
    queryKey: ['expense-categories', 'active'],
    queryFn: () => listExpenseCategories(true),
  });

  function changePo(next: string) {
    setPoId(next);
    // A different PO means different receipts — drop the receipt-driven rows.
    setChecked(new Set());
    setLines((prev) => mergeReceiptLines(prev, []));
  }

  function toggleReceipt(id: string, on: boolean) {
    const next = new Set(checked);
    if (on) next.add(id);
    else next.delete(id);
    setChecked(next);
    setLines((prev) => mergeReceiptLines(prev, linesFromReceipts(receipts, next)));
  }

  function updateLine(key: string, patch: Partial<ItemLine> | Partial<CategoryLine>) {
    setLines(
      (prev) =>
        prev.map((l) => {
          if (l.key !== key) return l;
          // Hand-editing a prefilled row detaches it from the receipts.
          return l.kind === 'item' ? { ...l, ...patch, fromReceipt: false } : { ...l, ...patch };
        }) as EditorLine[],
    );
  }

  function addCatalogItem() {
    const c = (catalogQ.data ?? []).find((x) => x.productSupplierId === pickCatalog);
    if (!c) return;
    const key = itemKey(c.productId, c.variantId);
    if (lines.some((l) => l.key === key)) {
      setLineHint('That product is already on the bill — adjust its quantity instead.');
      return;
    }
    setLineHint(null);
    setLines((prev) => [
      ...prev,
      {
        key,
        kind: 'item',
        productId: c.productId,
        variantId: c.variantId,
        label: c.variantName ? `${c.productName} · ${c.variantName}` : c.productName,
        qty: '1',
        unitCost: trimMoney(c.unitCost),
        fromReceipt: false,
      },
    ]);
    setPickCatalog('');
  }

  function addCategoryLine() {
    if (!pickCategory) return;
    setLineHint(null);
    setLines((prev) => [
      ...prev,
      {
        key: `cat:${crypto.randomUUID()}`,
        kind: 'category',
        categoryId: pickCategory,
        description: '',
        amount: '',
      },
    ]);
    setPickCategory('');
  }

  // ── totals (display only) ──
  const total = lines.reduce((s, l) => s + lineAmount(l), 0);
  const vatNum = vatOn ? Number(sanitizeMoney(vatAmount)) || 0 : 0;
  const subtotal = total - vatNum;

  const lineProblems = lines.some((l) =>
    l.kind === 'item'
      ? !(Number.isInteger(Number(l.qty)) && Number(l.qty) > 0) ||
        !(Number(sanitizeMoney(l.unitCost)) >= 0) ||
        l.unitCost.trim() === ''
      : !l.categoryId || !(Number(sanitizeMoney(l.amount)) > 0),
  );
  const vatProblem = vatOn && (vatNum < 0 || vatNum >= total);

  const save = useMutation({
    mutationFn: async (post: boolean) => {
      const body = {
        invoiceNo: invoiceNo.trim(),
        poId: poId || null,
        invoiceDate,
        dueDate: dueDate || null,
        vatAmount: vatOn && vatAmount.trim() ? sanitizeMoney(vatAmount) : null,
        memo: memo.trim() || null,
        lines: lines.map(toInput),
        ...(poId ? { receiptFulfillmentIds: [...checked] } : {}),
      };
      const row: SupplierInvoiceRow = draftId
        ? await updateSupplierInvoice(draftId, body)
        : await createSupplierInvoice({ ...body, supplierId });
      // Remember the saved draft BEFORE posting: if the post step fails, the
      // next Save updates this draft instead of re-creating (and 409ing on)
      // the same bill number.
      setDraftId(row.id);
      if (post) await postSupplierInvoice(row.id);
      return row;
    },
    meta: {
      successMessage: 'Bill saved.',
      silentErrorCodes: Object.keys(ERROR_COPY),
    },
    onSuccess: onSaved,
  });

  const canSubmit =
    !!supplierId &&
    !!invoiceNo.trim() &&
    !!invoiceDate &&
    lines.length > 0 &&
    !lineProblems &&
    !vatProblem &&
    !save.isPending;

  // Closing after a draft was created in this session must refresh the list
  // behind the dialog so the draft shows up with its Edit / Post actions.
  const close = () => (draftId && !initial ? onSaved() : onClose());

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && close()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initial
              ? `Edit draft ${initial.invoiceNo}`
              : draftId
                ? `Draft ${invoiceNo.trim() || 'saved'}`
                : 'New bill'}
          </DialogTitle>
          <DialogDescription>
            Enter the supplier&apos;s bill line by line. Link the PO and tick the deliveries it
            covers so the three-way match can check it — the total comes from the lines.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) save.mutate(false);
          }}
        >
          {/* ── header ── */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs text-muted-foreground">Supplier</label>
              <div className="flex items-center gap-2">
                <select
                  value={supplierId}
                  onChange={(e) => {
                    setSupplierId(e.target.value);
                    changePo('');
                    setLines([]);
                  }}
                  className={`${SELECT_CLS} flex-1`}
                  disabled={editing}
                  required
                >
                  <option value="">— Select supplier —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
                {supplier ? (
                  <span className="whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {supplier.paymentTermsDays != null
                      ? `Net ${supplier.paymentTermsDays}`
                      : 'Due on receipt'}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Bill number</label>
              <Input
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="Supplier's invoice no."
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">PO (optional)</label>
              <select
                value={poId}
                onChange={(e) => changePo(e.target.value)}
                className={`${SELECT_CLS} w-full`}
                disabled={!supplierId}
              >
                <option value="">
                  {!supplierId
                    ? '— Pick a supplier first —'
                    : pos.length === 0
                      ? '— No POs with unbilled receipts —'
                      : '— No PO —'}
                </option>
                {pos.map((p) => (
                  <option key={p.poId} value={p.poId}>
                    {p.poNumber} · {p.status} · received {peso(p.receivedValue)} · billed{' '}
                    {peso(p.billedTotal)}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                Only POs whose received value isn&apos;t yet covered by bills are listed.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Bill date</label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Due date (optional)</label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">
                Blank = from the supplier&apos;s terms (bill date + Net days, or due on receipt).
              </p>
            </div>
          </div>

          {/* ── receipts picker ── */}
          {poId ? (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Deliveries this bill covers</p>
                <span className="text-xs text-muted-foreground">
                  {checked.size} of {receipts.length} selected
                </span>
              </div>
              {receiptsQ.isLoading ? (
                <p className="text-xs text-muted-foreground">Loading receipts…</p>
              ) : receipts.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nothing has been received on this PO yet — the match will compare against the
                  whole PO.
                </p>
              ) : (
                <ul className="divide-y">
                  {receipts.map((r) => {
                    const other = r.claimedBy && r.claimedBy.invoiceId !== draftId;
                    const on = checked.has(r.fulfillmentId);
                    return (
                      <li key={r.fulfillmentId} className="py-2">
                        <label
                          className={`flex items-start gap-3 ${other ? 'opacity-60' : 'cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={on}
                            disabled={!!other}
                            onChange={(e) => toggleReceipt(r.fulfillmentId, e.target.checked)}
                          />
                          <div className="min-w-0 flex-1 text-sm">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <span className="font-mono text-xs">{r.refNumber ?? 'Receipt'}</span>
                              <span className="text-xs text-muted-foreground">
                                {shortDate(r.receivedAt)} · {r.branchName}
                              </span>
                              <Money>{peso(r.value)}</Money>
                              {other ? (
                                <Badge variant="muted">billed on {r.claimedBy!.invoiceNo}</Badge>
                              ) : null}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {r.lines
                                .map(
                                  (l) =>
                                    `${l.qty} × ${l.productName}${l.variantName ? ` (${l.variantName})` : ''} @ ${peso(l.unitCost)}`,
                                )
                                .join(' · ')}
                              {r.note ? ` — ${r.note}` : ''}
                            </p>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-[11px] text-muted-foreground">
                Ticking a delivery prefills its items below at the PO cost; adjust any line as the
                paper bill says.
              </p>
            </div>
          ) : null}

          {/* ── lines ── */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Lines</p>
            {lines.length === 0 ? (
              <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                No lines yet — add a product from the supplier&apos;s catalog or a category line
                (freight, fees, services).
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Line</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Unit cost</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.key} className="border-b last:border-0">
                        {l.kind === 'item' ? (
                          <>
                            <td className="px-3 py-1.5">
                              <span className="font-medium">{l.label}</span>
                              {l.fromReceipt ? (
                                <span className="ml-2 text-[11px] text-muted-foreground">
                                  from receipt
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <Input
                                type="number"
                                min={1}
                                step={1}
                                className={NUM_CLS}
                                value={l.qty}
                                onChange={(e) => updateLine(l.key, { qty: e.target.value })}
                              />
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <MoneyInput
                                className="h-9 w-28 text-right"
                                value={l.unitCost}
                                onChange={(e) => updateLine(l.key, { unitCost: e.target.value })}
                              />
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-1.5">
                              <div className="flex flex-col gap-1 sm:flex-row sm:items-center">
                                <span className="font-medium">
                                  {(categoriesQ.data ?? []).find((c) => c.id === l.categoryId)
                                    ?.name ?? 'Category'}
                                </span>
                                <Input
                                  className="h-8 sm:max-w-xs"
                                  placeholder="Description (optional)"
                                  value={l.description}
                                  onChange={(e) =>
                                    updateLine(l.key, { description: e.target.value })
                                  }
                                />
                              </div>
                            </td>
                            <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">
                              —
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <MoneyInput
                                className="h-9 w-28 text-right"
                                placeholder="0.00"
                                value={l.amount}
                                onChange={(e) => updateLine(l.key, { amount: e.target.value })}
                              />
                            </td>
                          </>
                        )}
                        <td className="px-3 py-1.5 text-right">
                          <Money>{peso(lineAmount(l))}</Money>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="flex gap-2">
                <select
                  value={pickCatalog}
                  onChange={(e) => setPickCatalog(e.target.value)}
                  className={`${SELECT_CLS} flex-1`}
                  disabled={!supplierId}
                >
                  <option value="">
                    {!supplierId
                      ? '— Pick a supplier first —'
                      : catalogQ.isLoading
                        ? 'Loading catalog…'
                        : (catalogQ.data ?? []).length === 0
                          ? '— No products sourced from this supplier —'
                          : '— Add a product —'}
                  </option>
                  {(catalogQ.data ?? []).map((c) => (
                    <option key={c.productSupplierId} value={c.productSupplierId}>
                      {c.productName}
                      {c.variantName ? ` · ${c.variantName}` : ''} ({c.sku}) @ {peso(c.unitCost)}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!pickCatalog}
                  onClick={addCatalogItem}
                >
                  Add item
                </Button>
              </div>
              <div className="flex gap-2">
                <select
                  value={pickCategory}
                  onChange={(e) => setPickCategory(e.target.value)}
                  className={`${SELECT_CLS} flex-1`}
                >
                  <option value="">— Add a category line —</option>
                  {(categoriesQ.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!pickCategory}
                  onClick={addCategoryLine}
                >
                  Add line
                </Button>
              </div>
            </div>
            {lineHint ? <p className="text-xs text-destructive">{lineHint}</p> : null}
          </div>

          {/* ── VAT + memo + totals ── */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={vatOn}
                  onChange={(e) => setVatOn(e.target.checked)}
                />
                VAT amount (optional)
              </label>
              {vatOn ? (
                <MoneyInput
                  value={vatAmount}
                  onChange={(e) => setVatAmount(e.target.value)}
                  placeholder="0.00"
                />
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Tick to record the VAT portion of the total (the lines are VAT-inclusive).
                </p>
              )}
              {vatProblem ? (
                <p className="text-xs text-destructive">VAT must be less than the total.</p>
              ) : null}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Memo</label>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
                  placeholder="Notes for this bill…"
                />
              </div>
            </div>
            <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal (ex-VAT)</span>
                <Money>{peso(subtotal)}</Money>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">VAT</span>
                <Money>{peso(vatNum)}</Money>
              </div>
              <div className="flex justify-between border-t pt-1 text-base font-semibold">
                <span>Total</span>
                <Money>{peso(total)}</Money>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {lines.length} line{lines.length === 1 ? '' : 's'}
                {poId ? ` · ${checked.size} delivery${checked.size === 1 ? '' : 'ies'} linked` : ''}
              </p>
            </div>
          </div>

          {save.error != null && (
            <p className="text-sm text-destructive">
              {draftId && !initial ? 'The draft is saved. ' : ''}
              {friendly(save.error, 'Failed to save the bill.')}
            </p>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={close} disabled={save.isPending}>
              {draftId && !initial ? 'Close' : 'Cancel'}
            </Button>
            <Button type="submit" variant="secondary" disabled={!canSubmit}>
              {save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
            <Button type="button" disabled={!canSubmit} onClick={() => save.mutate(true)}>
              {save.isPending ? 'Saving…' : 'Save & post'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── detail panel (expansion row under a bill) ───────────────────────────────

const BASIS_LABEL: Record<string, string> = {
  two_way: 'vs the whole PO',
  three_way: 'vs linked receipts',
};

export function BillDetailPanel({ invoiceId }: { invoiceId: string }) {
  const q = useQuery({
    queryKey: ['supplier-invoice-detail', invoiceId],
    queryFn: () => getSupplierInvoice(invoiceId),
  });
  const d = q.data;
  if (q.isLoading) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  if (!d) return <p className="p-4 text-sm text-destructive">Could not load this bill.</p>;
  return (
    <div className="grid gap-4 bg-muted/20 p-4 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Lines</p>
        {d.lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Recorded without line items (before line-level bills).
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-1 pr-3">Line</th>
                <th className="py-1 pr-3 text-right">Qty</th>
                <th className="py-1 pr-3 text-right">Unit cost</th>
                <th className="py-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="py-1 pr-3">
                    {l.kind === 'item' ? (
                      <>
                        {l.productName}
                        {l.variantName ? (
                          <span className="text-muted-foreground"> · {l.variantName}</span>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <Badge variant="secondary">{l.categoryName ?? 'Category'}</Badge>
                        {l.description ? (
                          <span className="ml-2 text-muted-foreground">{l.description}</span>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="py-1 pr-3 text-right">{l.qty ?? '—'}</td>
                  <td className="py-1 pr-3 text-right">
                    {l.unitCost ? <Money>{peso(l.unitCost)}</Money> : '—'}
                  </td>
                  <td className="py-1 text-right">
                    <Money>{peso(l.lineTotal)}</Money>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {d.receipts.length > 0 ? (
          <>
            <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Deliveries covered
            </p>
            <ul className="space-y-1 text-sm">
              {d.receipts.map((r) => (
                <li key={r.fulfillmentId} className="flex flex-wrap items-center gap-x-3">
                  <span className="font-mono text-xs">{r.refNumber ?? 'Receipt'}</span>
                  <span className="text-xs text-muted-foreground">
                    {shortDate(r.receivedAt)} · {r.branchName}
                  </span>
                  <Money>{peso(r.value)}</Money>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
      <div className="space-y-2 text-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Match</p>
        {d.status === 'draft' ? (
          <p className="text-muted-foreground">Computed when the bill is posted.</p>
        ) : d.matchStatus === 'no_po' || !d.matchStatus ? (
          <p className="text-muted-foreground">No PO linked.</p>
        ) : (
          <div className="space-y-1">
            <p>
              <Badge variant={d.matchStatus === 'matched' ? 'success' : 'warning'}>
                {d.matchStatus}
              </Badge>{' '}
              <span className="text-xs text-muted-foreground">
                {d.matchBasis ? BASIS_LABEL[d.matchBasis] : ''}
              </span>
            </p>
            {d.matchVariance != null ? (
              <p className="text-xs text-muted-foreground">
                Variance <Money>{peso(d.matchVariance)}</Money>
              </p>
            ) : null}
          </div>
        )}
        <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Posted
        </p>
        <p className="text-xs text-muted-foreground">
          {d.postedAt
            ? `${shortDate(d.postedAt)}${d.postedByName ? ` by ${d.postedByName}` : ''}`
            : 'Not posted yet'}
        </p>
        {d.memo ? (
          <>
            <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Memo
            </p>
            <p className="text-xs">{d.memo}</p>
          </>
        ) : null}
      </div>
    </div>
  );
}
